import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { habitInputSchema, MAX_DURATION_MINUTES, type HabitDto } from "@habitos/shared";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useHabits } from "../lib/queries";
import { WEEK_ORDER, WEEKDAY_SHORT } from "../lib/format";

const COLORS = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#ec4899", "#8b5cf6", "#64748b"];

export function HabitEditor() {
  const { id } = useParams();
  const habits = useHabits(true);
  const existing = id ? habits.data?.find((h) => h.id === id) : undefined;

  if (id && habits.isPending) return <p className="muted center">Cargando…</p>;
  if (id && !existing) return <p className="muted center">Este hábito no existe.</p>;
  return <HabitForm key={existing?.id ?? "new"} existing={existing} />;
}

function HabitForm({ existing }: { existing: HabitDto | undefined }) {
  const { today } = useAuth();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [days, setDays] = useState<number[]>(existing?.daysOfWeek ?? [1, 2, 3, 4, 5]);
  const [color, setColor] = useState(existing?.color ?? COLORS[0]!);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const done = async () => {
    await client.invalidateQueries({ queryKey: ["habits"] });
    navigate("/habitos");
  };

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const parsed = habitInputSchema.safeParse({
      title: form.get("title"),
      emoji: String(form.get("emoji") ?? "").trim() || null,
      color,
      time: form.get("time"),
      durationMinutes: Number(form.get("durationMinutes")),
      daysOfWeek: days,
      startDate: form.get("startDate"),
      endDate: String(form.get("endDate") ?? "") || null,
      reminder: form.get("reminder") === "on",
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Revisa los datos");
      return;
    }
    setBusy(true);
    try {
      if (existing) await api.updateHabit(existing.id, parsed.data);
      else await api.createHabit(parsed.data);
      await done();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  async function archive() {
    if (!existing) return;
    await api.archiveHabit(existing.id, !existing.archivedAt);
    await done();
  }

  async function remove() {
    if (!existing || !confirm("¿Borrar este hábito y todo su historial?")) return;
    await api.deleteHabit(existing.id);
    await done();
  }

  const toggleDay = (d: number) =>
    setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));

  return (
    <>
      <header className="page-header row start">
        <button className="icon-btn" aria-label="Volver" onClick={() => navigate(-1)}>‹</button>
        <h1>{existing ? "Editar hábito" : "Nuevo hábito"}</h1>
      </header>

      <form onSubmit={onSubmit} className="card form">
        <div className="row gap">
          <label className="emoji-field">
            Ícono
            <input name="emoji" defaultValue={existing?.emoji ?? ""} placeholder="📚" maxLength={8} />
          </label>
          <label className="grow">
            ¿Qué quieres hacer?
            <input name="title" required maxLength={80} defaultValue={existing?.title} placeholder="Leer 20 minutos" />
          </label>
        </div>

        <fieldset>
          <legend>¿Qué días?</legend>
          <div className="chips">
            {WEEK_ORDER.map((d) => (
              <button
                type="button"
                key={d}
                className={`chip ${days.includes(d) ? "on" : ""}`}
                aria-pressed={days.includes(d)}
                onClick={() => toggleDay(d)}
              >
                {WEEKDAY_SHORT[d]}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="row gap">
          <label className="grow">
            Hora
            <input name="time" type="time" required defaultValue={existing?.time ?? "08:00"} />
          </label>
          <label className="grow">
            Duración (min)
            <input name="durationMinutes" type="number" min={5} max={MAX_DURATION_MINUTES} step={5} defaultValue={existing?.durationMinutes ?? 30} />
          </label>
        </div>

        <div className="row gap">
          <label className="grow">
            Desde
            <input name="startDate" type="date" required defaultValue={existing?.startDate ?? today} />
          </label>
          <label className="grow">
            Hasta <span className="muted">(opcional)</span>
            <input name="endDate" type="date" defaultValue={existing?.endDate ?? ""} />
          </label>
        </div>

        <fieldset>
          <legend>Color</legend>
          <div className="chips">
            {COLORS.map((c) => (
              <button
                type="button"
                key={c}
                className={`swatch ${c === color ? "on" : ""}`}
                style={{ background: c }}
                aria-label={c}
                aria-pressed={c === color}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </fieldset>

        <label className="toggle">
          <input name="reminder" type="checkbox" defaultChecked={existing?.reminder ?? true} />
          Recordarme a esa hora
        </label>

        {error && <p className="error">{error}</p>}
        <button className="btn primary" disabled={busy}>{existing ? "Guardar" : "Crear hábito"}</button>
      </form>

      {existing && (
        <div className="row gap danger-zone">
          <button className="btn" onClick={archive}>{existing.archivedAt ? "Reactivar" : "Archivar"}</button>
          <button className="btn danger" onClick={remove}>Borrar</button>
        </div>
      )}
    </>
  );
}
